import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { environment } from '../../../environments/environment';
import { isPlatformBrowser } from '@angular/common';
import { UserStorage } from '../user-account/accounts.service';
import * as configcat from 'configcat-js-ssr';
import { StorageService } from '../storage';
import { StorageKeysEnum } from '../../shared/constants';

@Injectable({
  providedIn: 'root',
})
export class ConfigCatService {
  protected readonly isBrowser = isPlatformBrowser(this.platformId);
  private configCatClient: configcat.IConfigCatClient | null = null;
  private user: configcat.User | null = null;

  bookingTestingFlag = false;

  get bookingUrl() {
    return this.bookingTestingFlag ? '/booking/v2' : '/booking';
  }

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    private readonly storageService: StorageService
  ) {}

  init() {
    if (!this.isBrowser || this.configCatClient) return;

    const logger = configcat.createConsoleLogger(configcat.LogLevel.Info);

    this.configCatClient = configcat.getClient(
      environment.configCatSdkKey,
      configcat.PollingMode.AutoPoll,
      {
        logger,
        setupHooks: (hooks) =>
          hooks.on('flagEvaluated', (evaluationDetails) => {
            if (this.isBrowser) {
              const variant = `configcat-${evaluationDetails.key}-${evaluationDetails.value}`;
              (window as any).dataLayer.push({
                event: 'abTestVariation',
                variation: variant,
                variation_id: evaluationDetails.variationId,
              });
            }
          }),
      }
    );
  }

  async getBookingTestingFlag() {
    return await this.getFlagValue('pasarelatesting', false);
  }

  async getAppAdFlag() {
    return await this.configCatClient!.getValueAsync('appAdvertisement', false);
  }

  async getRedirectToPackagesFlag() {
    return await this.getFlagValue('redirectToPackages', false);
  }

  private async getFlagValue<T extends configcat.SettingValue>(key: string, defaultValue: T) {
    if (!this.configCatClient) {
      console.warn('ConfigCat client not initialized');
      return defaultValue;
    }

    const user = this.getUserObject();
    return await this.configCatClient.getValueAsync<T>(key, defaultValue, user);
  }

  private getUserObject(): configcat.User {
    if (this.user) return this.user;

    const user = this.storageService.getItem<UserStorage>(StorageKeysEnum.USER);

    let userId = user?.id?.toString(10) ?? null;

    if (!userId) {
      userId = this.storageService.getItem<string>(StorageKeysEnum.CONFIGCAT_USER_ID);

      if (!userId) {
        userId = this.generateRandomId();
        this.storageService.setItem<string>(StorageKeysEnum.CONFIGCAT_USER_ID, userId);
      }
    }

    this.user = new configcat.User(userId, user?.email, 'PE');
    return this.user;
  }

  private generateRandomId(): string {
    return (Math.random() + 1).toString(36).substring(7);
  }
}
